/**
 * RecommendedBooksVerticalSection
 * 
 * Displays 10 books vertically with AI-generated hyper-personalized messages
 * - Full-width book cards
 * - On-demand personalization if existing reason doesn't mention user name
 * - Typewriter animation on first view
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { View, StyleSheet, ActivityIndicator } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { Text, Theme, BookCard } from '../../../components/ui';
import BookPersonalizationMessage from '../../../components/ui/BookPersonalizationMessage';
import { FireBook } from '../../../models/FirestoreModels';
import { auth } from '../../../config/firebase';
import { PrivateProfileService } from '../../../services/privateProfileService';
import { BookService } from '../../../services/bookService';
import { mentorChatService, MentorChatService } from '../../../services/mentorChatService';
import { useBookAnimationTracking } from '../../../hooks/useBookAnimationTracking';

interface BookWithMessage {
  book: FireBook;
  message: string;
  isPersonalized: boolean;
}

const RecommendedBooksVerticalSection: React.FC = () => {
  const navigation = useNavigation();
  const [booksWithMessages, setBooksWithMessages] = useState<BookWithMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [personalizing, setPersonalizing] = useState(false);
  const [userName, setUserName] = useState<string>('');
  const { hasAnimated, markAsAnimated } = useBookAnimationTracking();

  // Load recommended books and generate personalized messages
  useEffect(() => {
    const loadBooks = async () => {
      try {
        const user = auth.currentUser;
        if (!user) {
          setLoading(false);
          return;
        }

        // Get user profile
        const profileResult = await PrivateProfileService.getProfile(user.uid);
        if (!profileResult.success || !profileResult.data) {
          setLoading(false);
          return;
        }

        const profile = profileResult.data;
        // Try profile displayName first, then auth displayName, finally fallback to 'You'
        const displayName = profile.displayName || user.displayName || 'You';
        const firstName = displayName.split(' ')[0];
        setUserName(firstName);

        // Get favorited books to filter out
        const booksProgress = profile.booksProgress || {};
        const favoriteIds = Object.entries(booksProgress)
          .filter(([_, progress]) => progress.isFavorite === true)
          .map(([bookId]) => bookId.split('_')[0]);

        // Get book recommendations
        const recommendedBooks = profile.currentRecommendations?.recommendedBooks || [];
        
        if (recommendedBooks.length === 0) {
          setLoading(false);
          return;
        }

        // Take first 10 books (excluding favorited) and normalize bookId to base (no language suffix)
        const bookIdsToFetch = recommendedBooks
          .filter(rec => !favoriteIds.includes(rec.bookId.split('_')[0]))
          .slice(0, 10)
          .map(rec => ({
            ...rec,
            bookId: rec.bookId.split('_')[0], // normalize to base to avoid _en_en
          }));

        if (bookIdsToFetch.length === 0) {
          setLoading(false);
          return;
        }

        // Language code (default to 'en' for now)
        const languageCode = 'en';

        // Batch fetch all books (uses cache for speed)
        const bookIds = bookIdsToFetch.map(rec => rec.bookId);
        const booksResult = await BookService.getBooksById(bookIds, languageCode);
        
        const fetchedBooks = booksResult.success && booksResult.data 
          ? booksResult.data 
          : [];

        // Show books immediately with existing reasons
        const initialBooks = fetchedBooks.map((book, index) => {
          const recommendation = bookIdsToFetch[index];
          return {
            book,
            message: recommendation.reason || '',
            isPersonalized: recommendation.reason?.includes(firstName) || false,
          };
        });
        
        setBooksWithMessages(initialBooks);
        setLoading(false);

        // Generate personalized messages in background (single batch to reduce repetition)
        const userProfile = MentorChatService.createUserProfile(profile);

        const booksToPersonalize = fetchedBooks
          .map((book, index) => {
            const recommendation = bookIdsToFetch[index];
            const hasPersonalization = (recommendation.reason || '').includes(firstName);
            return { book, hasPersonalization };
          })
          .filter(item => !item.hasPersonalization);

        if (booksToPersonalize.length > 0) {
          setPersonalizing(true);
          console.log(`[RecommendedBooksVertical] Generating batch personalization for ${booksToPersonalize.length} books`);

          const booksWithContext = await Promise.all(booksToPersonalize.map(async ({ book }) => {
            const bookDescription = book.biggestTakeaway || book.introduction || book.introductionMarkdown;
            const baseBookId = book.id.split('_')[0];

            let insightTitles: string[] | undefined;
            if (book.insightIds && book.insightIds.length > 0) {
              try {
                // Use BookService to get insights (uses cache)
                const bookDetailResult = await BookService.getBookDetail(baseBookId, languageCode);
                if (bookDetailResult.success && bookDetailResult.data?.insights) {
                  insightTitles = bookDetailResult.data.insights
                    .slice(0, 5)
                    .map(insight => insight.title);
                }
              } catch (error) {
                console.warn('[RecommendedBooksVertical] Failed to fetch insights:', error);
                insightTitles = undefined;
              }
            }

            return {
              bookId: baseBookId, // use base id for gateway + persistence
              fullBookId: book.id, // keep full id for UI mapping
              title: book.title,
              author: book.author,
              description: bookDescription,
              insightTitles,
            };
          }));

          const result = await mentorChatService.generateBookPersonalizationBatch(
            booksWithContext.map(({ fullBookId: _full, ...rest }) => rest),
            userProfile,
            firstName
          );

          if (result.success && result.messages) {
            const entries = Object.entries(result.messages);
            const fullIdLookup = booksWithContext.reduce<Record<string, string>>((acc, item) => {
              acc[item.bookId] = item.fullBookId;
              return acc;
            }, {});
            const messageByFullId = Object.entries(result.messages).reduce<Record<string, string>>((acc, [baseId, msg]) => {
              const fullId = fullIdLookup[baseId] || baseId;
              acc[fullId] = msg;
              return acc;
            }, {});

            if (entries.length > 0) {
              // Persist personalized reasons
              await Promise.all(entries.map(async ([bookId, personalizedMessage]) => {
                const baseBookId = bookId.split('_')[0];
                try {
                  await PrivateProfileService.updateBookRecommendationReason(
                    user.uid,
                    baseBookId,
                    personalizedMessage
                  );
                  console.log(`[RecommendedBooksVertical] Saved personalized message for: ${baseBookId}`);
                } catch (error) {
                  console.warn('[RecommendedBooksVertical] Failed to save personalized message:', error);
                }
              }));

              // Update UI state with new personalized messages
              setBooksWithMessages(prev => 
                prev.map(item => 
                  messageByFullId[item.book.id]
                    ? { ...item, message: messageByFullId[item.book.id], isPersonalized: true }
                    : item
                )
              );
          }

          setPersonalizing(false);
          }

        }

        console.log(`[RecommendedBooksVertical] Loaded ${fetchedBooks.length} books (messages generating in background)`);
      } catch (error) {
        console.error('[RecommendedBooksVertical] Error loading books:', error);
      } finally {
        setLoading(false);
      }
    };

    loadBooks();
  }, []);

  const handleBookPress = useCallback((book: FireBook) => {
    const bookId = book.id.split('_')[0];
    (navigation as any).navigate('BookDetail', { bookId });
  }, [navigation]);

  const handleAnimationComplete = useCallback((bookId: string) => {
    markAsAnimated(bookId);
  }, [markAsAnimated]);

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={Theme.colors.primary} />
      </View>
    );
  }

  if (booksWithMessages.length === 0) {
    return null;
  }

  return (
    <View style={styles.container}>
      {/* Section Header */}
      <View style={styles.header}>
        <Text style={styles.headerTitle}>Chosen for Your Plan</Text>
        <Text style={styles.headerSubtitle}>Your mentor curates the ideas • Books power your plan</Text>
      </View>

      {/* Vertical Book List */}
      <View style={styles.booksList}>
        {booksWithMessages.map((item, index) => {
          const insightCount = item.book.insightIds?.length || item.book.statistics?.insights || 0;
          const actionLabel = insightCount > 0 
            ? `${insightCount} idea${insightCount !== 1 ? 's' : ''} from this book chosen for you`
            : '';
          
          return (
            <BookCard
              key={item.book.id}
              variant="vertical"
              title={item.book.title}
              author={item.book.author}
              coverUrl={item.book.coverImageUrl || item.book.coverImageFullUrl}
              insightsCount={item.book.insightIds?.length || item.book.statistics?.insights}
              onPress={() => handleBookPress(item.book)}
              style={styles.bookCard}
              actionLabel={actionLabel}
            >
              {item.message ? (
                <BookPersonalizationMessage
                  bookId={item.book.id}
                  message={item.message}
                  shouldAnimate={!hasAnimated(item.book.id)}
                  delayMs={index * 800} // Stagger by 800ms per book
                  onAnimationComplete={() => handleAnimationComplete(item.book.id)}
                />
              ) : personalizing ? (
                <View style={styles.placeholderRow}>
                  <ActivityIndicator size="small" color={Theme.colors.primary} />
                </View>
              ) : null}
            </BookCard>
          );
        })}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: Theme.spacing.xl,
  },
  loadingContainer: {
    padding: Theme.spacing.xl,
    alignItems: 'center',
    justifyContent: 'center',
  },
  header: {
    paddingHorizontal: Theme.spacing.lg,
    marginBottom: Theme.spacing.lg,
  },
  headerTitle: {
    fontSize: 24,
    lineHeight: 32,
    fontFamily: Theme.typography.fontFamily.bold,
    color: Theme.colors.sectionHeader,
    marginBottom: Theme.spacing.xs,
  },
  headerSubtitle: {
    fontSize: 14,
    fontFamily: Theme.typography.fontFamily.regular,
    color: Theme.colors.textSecondary,
  },
  booksList: {
    paddingHorizontal: Theme.spacing.lg,
    gap: Theme.spacing.md,
  },
  bookCard: {
    marginBottom: Theme.spacing.sm,
  },
  placeholderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: Theme.spacing.xs,
  },
});

export default RecommendedBooksVerticalSection;

